/**
 * collection-with-sidebar（.coll-sidebar-pm-compact）および carousel-shop-the-look（.xo-shop-the-look-unit）
 * - ロゴ等ヘッダーを xo:carousel:change / init でフェード（トップ index と同様）
 * - アクティブスライドの xo-center（奇数 perView 時に付与）を除去し、3 枚目以降が中央寄りになるのを防ぐ
 * - トップ SP: product media の縦中央に「>」（--coll-sidebar-next-top）— index-collection-sidebar-product-media.css と併用
 */
(function () {
  const UNIT_SELECTOR = [
    '.xo-section.coll-sidebar-pm-compact .xo-coll-sidebar-carousel-unit',
    '.xo-shop-the-look-unit',
  ].join(', ');
  const SLIDE_SEL = 'xo-carousel-slide, [xo-carousel-slide], xo-item';

  function isHomeTemplate() {
    return document.body && document.body.hasAttribute('xo-home');
  }

  function isMobileCarouselNavLayout() {
    return window.matchMedia('(max-width: 1131px)').matches;
  }

  function pickProductMediaInActiveSlide(carousel) {
    if (!(carousel instanceof Element)) return null;
    const active =
      carousel.querySelector('xo-carousel-slide[xo-active], [xo-carousel-slide][xo-active]') ||
      carousel.querySelector('xo-carousel-slide:not([xo-cloned]), [xo-carousel-slide]:not([xo-cloned])');
    return active?.querySelector('.xo-card__media') ?? null;
  }

  function updateCollSidebarNextButtonY(carousel) {
    if (!(carousel instanceof HTMLElement)) return;
    const layout = carousel.closest('.xo-layout-carousel');
    if (!(layout instanceof HTMLElement)) return;
    if (!isHomeTemplate() || !isMobileCarouselNavLayout()) {
      layout.style.removeProperty('--coll-sidebar-next-top');
      return;
    }
    const media = pickProductMediaInActiveSlide(carousel);
    const nextBtn = carousel.querySelector('xo-carousel-next');
    if (!media || !nextBtn) return;
    const lRect = layout.getBoundingClientRect();
    const mRect = media.getBoundingClientRect();
    if (mRect.height < 1 || lRect.height < 1) return;
    const centerY = mRect.top - lRect.top + mRect.height / 2;
    layout.style.setProperty('--coll-sidebar-next-top', `${Math.round(centerY)}px`);
  }

  function readCarouselSpeedMs(carousel) {
    const raw = carousel.getAttribute('xo-speed');
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : 400;
  }

  function stripSlideCenter(list) {
    if (!list) return;
    list.querySelectorAll(SLIDE_SEL).forEach((el) => {
      el.removeAttribute('xo-center');
    });
  }

  function watchSlideCenter(list) {
    if (!(list instanceof Element) || list.dataset.collSidebarStripCenter === '1') return;
    list.dataset.collSidebarStripCenter = '1';
    stripSlideCenter(list);
    const mo = new MutationObserver((mutations) => {
      let needsStrip = false;
      for (const m of mutations) {
        if (m.type === 'childList') {
          needsStrip = true;
          break;
        }
        if (m.type === 'attributes' && m.attributeName === 'xo-center') {
          const t = m.target;
          if (t instanceof Element && t.matches(SLIDE_SEL) && t.hasAttribute('xo-center') && t.getAttribute('xo-center') !== 'false') {
            needsStrip = true;
            break;
          }
        }
      }
      if (needsStrip) stripSlideCenter(list);
    });
    mo.observe(list, { subtree: true, childList: true, attributes: true, attributeFilter: ['xo-center'] });
  }

  function attachUnit(unit) {
    if (!(unit instanceof HTMLElement)) return;
    if (unit.dataset.collSidebarLogoSync === '1') return;

    const carousel = unit.querySelector('xo-carousel');
    const header = unit.querySelector('.xo-coll-sidebar-carousel-header, .xo-shop-the-look-carousel-header');
    if (!carousel) return;

    unit.dataset.collSidebarLogoSync = '1';

    const list = carousel.querySelector('xo-carousel-list');
    watchSlideCenter(list);

    let fadeTimer = null;

    const applyFade = () => {
      if (!header) return;
      const durationMs = readCarouselSpeedMs(carousel);
      const legMs = Math.max(60, Math.floor(durationMs / 2));
      header.style.setProperty('--xo-coll-sidebar-header-fade', `${legMs}ms`);

      if (fadeTimer) window.clearTimeout(fadeTimer);
      header.classList.remove('is-coll-sidebar-carousel-fading');

      window.requestAnimationFrame(() => {
        header.classList.add('is-coll-sidebar-carousel-fading');
        fadeTimer = window.setTimeout(() => {
          header.classList.remove('is-coll-sidebar-carousel-fading');
          fadeTimer = null;
        }, legMs);
      });
    };

    let alignNextRaf = null;
    const scheduleAlignNextY = () => {
      if (!isHomeTemplate()) return;
      if (alignNextRaf) window.cancelAnimationFrame(alignNextRaf);
      alignNextRaf = window.requestAnimationFrame(() => {
        alignNextRaf = null;
        updateCollSidebarNextButtonY(carousel);
      });
    };

    const onCarouselUpdate = () => {
      stripSlideCenter(list);
      applyFade();
      scheduleAlignNextY();
    };

    carousel.addEventListener('xo:carousel:init', onCarouselUpdate);
    carousel.addEventListener('xo:carousel:change', onCarouselUpdate);
    window.requestAnimationFrame(() => stripSlideCenter(list));
    window.setTimeout(() => stripSlideCenter(list), 0);
    window.setTimeout(() => stripSlideCenter(list), 80);

    if (isHomeTemplate()) {
      const ro = new ResizeObserver(() => scheduleAlignNextY());
      ro.observe(carousel);
      const bindImgLoads = () => {
        const m = pickProductMediaInActiveSlide(carousel);
        if (!m) return;
        m.querySelectorAll('img').forEach((img) => {
          if (img.complete) return;
          img.addEventListener('load', scheduleAlignNextY, { once: true });
        });
      };
      const scheduleAlignWithImages = () => {
        scheduleAlignNextY();
        bindImgLoads();
      };
      scheduleAlignWithImages();
      window.setTimeout(scheduleAlignWithImages, 120);
      window.setTimeout(scheduleAlignWithImages, 400);
    }
  }

  function scan(root) {
    const base = root && root.nodeType === 1 ? root : document;
    base.querySelectorAll(UNIT_SELECTOR).forEach(attachUnit);
  }

  function boot() {
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (target && target.nodeType === 1) scan(target);
  });
})();
